import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import LOGO512 from "../assets/images/logo512.png";
import LOGO_TEXT from "../assets/images/logo-text.png";
// ย่อจาก cl-bg-bw.jpg (2400px, 2.5MB) เหลือ 320px เพราะใน navbar แสดงแค่ ~50px
import CMRU_LOGO from "../assets/images/cmru/cl-bg-bw-320.jpg";
import { appSystems, appVersion, getAvailableSystems } from "./global";
import { GET, POST, PUT, loading, alert, startTokenRefreshScheduler } from "./CustomComponent";

const shellBackground = {
  background:
    "linear-gradient(180deg, rgba(244,250,252,0.96) 0%, rgba(223,238,245,0.88) 100%)",
  border: "1px solid rgba(255,255,255,0.44)",
  backdropFilter: "blur(24px)",
  WebkitBackdropFilter: "blur(24px)",
};

const iconStyle = {
  width: 34,
  height: 34,
  borderRadius: 12,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "FontAwesome, var(--app-font)",
  fontSize: 15,
  flexShrink: 0,
};

// โลโก้ของระบบแยก (key ตรงกับ appSystems) ระบบหลักใช้โลโก้ ParthenonTech ที่ฝังไว้ใน JSX อยู่แล้ว
const systemLogos = { cmru: CMRU_LOGO };

// ความกว้าง sidebar จอใหญ่: โหมดปกติ / โหมดย่อ (เหลือแค่โลโก้กับไอคอน)
// ค่าที่ใช้อยู่จะถูกส่งให้ AppShell ผ่าน CSS variable --ptn-sidebar-width เพื่อขยับเนื้อหาตาม
const SIDEBAR_WIDTH = 284;
const SIDEBAR_COLLAPSED_WIDTH = 96;
const SIDEBAR_STORAGE_KEY = "ptn-nav-collapsed";
const sidebarEasing = "cubic-bezier(0.4, 0, 0.2, 1)";

let desktopMenuScrollTop = 0;
let mobileMenuScrollTop = 0;
let hasShownProfileError = false;

const getProfile = () => {
  const redirectToLogin = () => {
    if (hasShownProfileError) {
      return;
    }

    hasShownProfileError = true;
    alert("", "error", "ผิดพลาด", "โหลดข้อมูลผู้ใช้ไม่สำเร็จ กรุณาเข้าสู่ระบบใหม่", "ตกลง").then(() => {
      localStorage.clear();
      window.location.href = "/";
    });
  };

  try {
    const rawProfile = localStorage.getItem("ptn-profile");
    if (!rawProfile) {
      redirectToLogin();
      return null;
    }

    return JSON.parse(rawProfile);
  } catch (error) {
    redirectToLogin();
    return null;
  }
};

// system = ระบบที่ navbar นี้กำลังแสดง ("main" | "cmru") ดูรายการระบบที่ appSystems ใน global.js
export default function NavbarComponent({ title = "Dashboard", description = "", system = "main" }) {
  const activeSystem = appSystems[system] || appSystems.main;
  const theme = activeSystem.theme;
  const isMainSystem = activeSystem.key === "main";
  const [showSystemMenu, setShowSystemMenu] = useState(false);
  // โหมดย่อ sidebar (จอใหญ่) จำค่าไว้ข้ามการรีเฟรช
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_STORAGE_KEY) === "1";
    } catch (error) {
      return false;
    }
  });
  // tooltip ของเมนูตอนย่อ: วาดแบบ fixed นอกกล่องเมนูที่ scroll ได้ ไม่งั้นจะถูกขอบกล่องตัด
  const [menuTip, setMenuTip] = useState(null);
  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;
  // profile อ่านสดจาก localStorage ทุก render ตัวนี้มีไว้สั่ง render ใหม่เมื่อโหลดโปรไฟล์ล่าสุดมาแล้วสิทธิ์เข้าระบบเปลี่ยน
  const [, setProfileRevision] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const desktopMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const profile = getProfile();
  // ต่อชื่อ-นามสกุลเฉพาะส่วนที่มีค่า
  // ถ้าเขียน a + " " + b || "-" ตัว + จะทำงานก่อน || เลยได้ "undefined undefined" ซึ่งเป็น truthy -> "-" ไม่มีวันทำงาน
  const displayName = [profile?.employee?.first_name, profile?.employee?.last_name].filter(Boolean).join(" ") || "-";
  const initials = (displayName || "-")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

  const menuGroups = useMemo(() => {
    const groups = [];
    const appPermission = profile?.role?.permission ? ["welcome.read", ...profile?.role?.permission] : [];
    // super_user เห็นทุกเมนูเสมอ (backend ให้ผ่านทุกสิทธิ์อยู่แล้ว) ไม่ต้องพึ่งรายการสิทธิ์ของ role
    const isSuperUser = profile?.user_type === "super_user";
    
    let currentGroup = null;

    activeSystem.menu.forEach((item) => {
      if (item.title) {
        currentGroup = { title: item.title, items: [] };
        groups.push(currentGroup);
        return;
      }

      if (!currentGroup) {
        currentGroup = { title: "Menu", items: [] };
        groups.push(currentGroup);
      }
      
      // เมนูที่ติดธง superOnly เปิดให้เฉพาะ super_user ต่อให้ role มีสิทธิ์ครบก็ไม่เห็น (backend กันอีกชั้นด้วย checkSuperUser)
      if (item.superOnly && !isSuperUser) return;

      // ระบบแยก (เช่น CMRU) ไม่ผูกกับ permission: ผ่านด่านเข้าระบบมาแล้วเห็นทุกเมนู
      if(!activeSystem.usesPermission || isSuperUser || appPermission.includes(item.moduleKey+".read")){
        currentGroup.items.push(item);
      }
    });
    
    return groups.filter((group) => group.items.length > 0);
    // ต้องผูกกับสิทธิ์ ไม่งั้นเมนูค้างของเดิมเมื่อสิทธิ์เปลี่ยนกลางเซสชัน
  }, [profile?.role?.permission, profile?.user_type, activeSystem]);

  // ระบบที่บัญชีนี้สลับไปได้ (ดู profile.systems[key] ใน global.js) มีระบบเดียวก็ไม่ต้องแสดงตัวสลับ
  const availableSystems = getAvailableSystems(profile);

  const handleSwitchSystem = (target) => {
    setShowSystemMenu(false);
    setMobileOpen(false);
    if (target.key !== activeSystem.key) navigate(target.home);
  };

  // ต่ออายุ token อัตโนมัติก่อนหมดอายุ (มีตัวจับเวลาแค่ตัวเดียวทั้งแอป เรียกซ้ำไม่เป็นไร)
  useEffect(() => {
    startTokenRefreshScheduler();
  }, []);

  // ส่งความกว้าง sidebar ให้ layout (AppShell ใช้ขยับ margin ของเนื้อหา) ตั้งก่อน paint จะได้ไม่กระตุกตอนโหลดหน้า
  useLayoutEffect(() => {
    document.documentElement.style.setProperty("--ptn-sidebar-width", `${sidebarWidth}px`);
  }, [sidebarWidth]);

  const toggleCollapsed = () => {
    setMenuTip(null);
    setShowSystemMenu(false);
    setCollapsed((value) => {
      const next = !value;
      try {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, next ? "1" : "0");
      } catch (error) {
        // โหมดส่วนตัว/บล็อก storage: ใช้ได้แค่ในเซสชันนี้
      }
      return next;
    });
  };

  const showMenuTip = (event, label) => {
    if (!collapsed) return;
    const rect = event.currentTarget.getBoundingClientRect();
    setMenuTip({ label, top: rect.top + rect.height / 2 });
  };

  const hideMenuTip = () => setMenuTip(null);

  useEffect(() => {
    if (desktopMenuRef.current) {
      desktopMenuRef.current.scrollTop = desktopMenuScrollTop;
    }

    if (mobileMenuRef.current) {
      mobileMenuRef.current.scrollTop = mobileMenuScrollTop;
    }
    const getProfile = async () => {
      const result = await GET("v1/admin/users/profile");
      if (result?.data) {
        // สิทธิ์เข้าระบบแยกเปลี่ยน (เช่น เพิ่งได้สิทธิ์ CMRU / กินอะไรดี) ให้ render ตัวสลับระบบใหม่
        const systemKeys = (item) => getAvailableSystems(item).map((system) => system.key).join(",");
        const hadSystems = systemKeys(profile);
        localStorage.setItem("ptn-profile", JSON.stringify(result.data));
        if (hadSystems !== systemKeys(result.data)) setProfileRevision((value) => value + 1);
      }
    };

     getProfile();
  }, [location.pathname, mobileOpen]);

  

  const handleDesktopMenuScroll = (event) => {
    desktopMenuScrollTop = event.currentTarget.scrollTop;
    if (menuTip) setMenuTip(null);
  };

  const handleMobileMenuScroll = (event) => {
    mobileMenuScrollTop = event.currentTarget.scrollTop;
  };

  // เปลี่ยนรหัสผ่านตัวเอง: สำเร็จแล้ว backend จะล้าง token ทิ้ง ต้องเด้งไปล็อกอินใหม่
  const handleChangePassword = async () => {
    const { current_password, new_password, confirm_password } = passwordForm;

    if (!current_password.trim() || !new_password.trim()) {
      await alert("", "warning", "แจ้งเตือน", "กรุณากรอกรหัสผ่านให้ครบ");
      return;
    }
    if (new_password.length < 8) {
      await alert("", "warning", "แจ้งเตือน", "รหัสผ่านใหม่ต้องยาวอย่างน้อย 8 ตัวอักษร");
      return;
    }
    if (new_password !== confirm_password) {
      await alert("", "warning", "แจ้งเตือน", "รหัสผ่านใหม่และการยืนยันไม่ตรงกัน");
      return;
    }
    if (!(await alert("", "info", "เปลี่ยนรหัสผ่าน", "หลังเปลี่ยนรหัสผ่านจะต้องเข้าสู่ระบบใหม่ ต้องการดำเนินการต่อ?", "เปลี่ยนรหัสผ่าน", "ยกเลิก"))) return;

    loading(true);
    const result = await PUT("v1/admin/users/change-password", {
      current_password: btoa(current_password),
      new_password: btoa(new_password),
    });
    loading(false);

    if (result?.status) {
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
      setShowProfileModal(false);
      await alert("", "success", "สำเร็จ", "เปลี่ยนรหัสผ่านเรียบร้อย กรุณาเข้าสู่ระบบใหม่");
      localStorage.removeItem("ptn-acc-tk");
      localStorage.removeItem("ptn-profile");
      navigate("/", { replace: true });
    } else {
      await alert("", "warning", "แจ้งเตือน", result?.message || "เปลี่ยนรหัสผ่านไม่สำเร็จ");
    }
  };

  const handleLogout = async () => {
    const confirmed = await alert(
      "",
      "warning",
      "ยืนยัน",
      "ต้องการออกจากระบบใช่หรือไม่?",
      "ออกจากระบบ",
      "ยกเลิก"
    );

    if (!confirmed) {
      return;
    }

    let result = await POST("v1/auth/logout");
    if (!result?.status) {
      alert("", "error", "ผิดพลาด", result?.message || "ออกจากระบบไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
      return;
    }

    localStorage.clear();
    
    await alert("", "success", "ออกจากระบบแล้ว", "ออกจากระบบเรียบร้อยแล้ว", "ตกลง");
    window.location.href = "/";
  };

  // compact = โหมดย่อ: เหลือแค่ไอคอนตรงกลาง ชื่อเมนูไปโชว์เป็น tooltip แทน
  const renderMenuLink = (item, compact = false) => (
    <NavLink
      key={item.link}
      to={item.link}
      onClick={() => {
        setMobileOpen(false);
        setMenuTip(null);
      }}
      onMouseEnter={compact ? (event) => showMenuTip(event, item.name) : undefined}
      onMouseLeave={compact ? hideMenuTip : undefined}
      aria-label={compact ? item.name : undefined}
      className="text-decoration-none"
      style={({ isActive }) =>
        compact
          ? {
              // โหมดย่อ: ไม่มีกรอบรอบนอก เหลือแค่ตัวไอคอน
              display: "flex",
              justifyContent: "center",
              padding: 0,
              margin: "0 auto",
              background: "transparent",
            }
          : {
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "6px 8px",
              borderRadius: 16,
              color: isActive ? "#f8fdff" : "#0f3551",
              background: isActive ? theme.gradient : "rgba(255, 255, 255, 0.86)",
              boxShadow: isActive
                ? `0 14px 28px ${theme.shadow}`
                : "inset 0 0 0 1px rgba(15, 53, 81, 0.08)",
              transition:
                "transform 180ms ease, box-shadow 180ms ease, background 180ms ease",
            }
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={compact ? "ptn-nav-compact-icon" : undefined}
            style={{
              ...iconStyle,
              background: isActive ? (compact ? theme.gradient : "rgba(255,255,255,0.16)") : theme.soft,
              color: isActive ? "#ffffff" : theme.solid,
              boxShadow: compact && isActive ? `0 10px 22px ${theme.shadow}` : undefined,
            }}
          >
            {item.icon}
          </span>
          {!compact ? (
            <span
              className="fw-semibold text-truncate"
              style={{ fontSize: "0.93rem", lineHeight: 1.2 }}
            >
              {item.name}
            </span>
          ) : null}
        </>
      )}
    </NavLink>
  );

  // ตัวสลับระบบ: อยู่ใต้โลโก้ (จอใหญ่) และบนสุดของเมนู (มือถือ) ตำแหน่งเดียวกันทุกระบบ กดกลับได้จากที่เดิม
  const renderSystemSwitcher = (compact = false) => {
    if (availableSystems.length < 2) return null;

    const listStyle = compact
      ? { left: "calc(100% + 14px)", top: 0, width: 250, zIndex: 1045, border: "1px solid rgba(0,0,0,0.05)" }
      : { top: "calc(100% + 6px)", zIndex: 1045, border: "1px solid rgba(0,0,0,0.05)" };

    return (
      <div className="position-relative" style={compact ? { width: 34, margin: "0 auto" } : undefined}>
        <button
          type="button"
          onClick={() => {
            setMenuTip(null);
            setShowSystemMenu((open) => !open);
          }}
          onMouseEnter={compact ? (event) => showMenuTip(event, `สลับระบบ · ${activeSystem.name}`) : undefined}
          onMouseLeave={compact ? hideMenuTip : undefined}
          aria-label={compact ? "สลับระบบ" : undefined}
          className={`btn d-flex align-items-center border-0 ${compact ? "justify-content-center p-0" : "w-100 gap-2 text-start"}`}
          aria-haspopup="listbox"
          aria-expanded={showSystemMenu}
          style={
            compact
              ? { borderRadius: 12, background: "transparent" }
              : {
                  padding: "7px 10px",
                  borderRadius: 16,
                  background: "rgba(255, 255, 255, 0.9)",
                  boxShadow: `inset 0 0 0 1px ${theme.shadow}`,
                }
          }
        >
          <span
            className={compact ? "ptn-nav-compact-icon" : undefined}
            style={{
              ...iconStyle,
              background: theme.gradient,
              color: "#ffffff",
              boxShadow: compact ? `0 8px 18px ${theme.shadow}` : undefined,
            }}
          >
            {activeSystem.icon}
          </span>
          {!compact ? (
            <>
              <span className="flex-grow-1 overflow-hidden">
                <span className="d-block fw-bold text-truncate" style={{ color: "#12314d", fontSize: "0.9rem", lineHeight: 1.2 }}>{activeSystem.name}</span>
                <span className="d-block text-truncate" style={{ color: "#6e8c9c", fontSize: 11 }}>{activeSystem.caption}</span>
              </span>
              <span className="icon" style={{ color: "#6e8c9c", fontSize: 12 }}>{"\uf0dc"}</span>
            </>
          ) : null}
        </button>

        {showSystemMenu && (
          <>
            <div style={{ position: "fixed", inset: 0, zIndex: 1040 }} onClick={() => setShowSystemMenu(false)} />
            <div
              className={`position-absolute bg-white rounded-4 shadow p-2 ${compact ? "" : "start-0 end-0"}`}
              role="listbox"
              style={listStyle}
            >
              <div className="px-2 pb-1 text-uppercase fw-semibold text-10" style={{ color: "#5e8192", letterSpacing: "0.08em" }}>สลับระบบ</div>
              {availableSystems.map((item) => {
                const isCurrent = item.key === activeSystem.key;
                return (
                  <button
                    key={item.key}
                    type="button"
                    role="option"
                    aria-selected={isCurrent}
                    onClick={() => handleSwitchSystem(item)}
                    className="btn w-100 d-flex align-items-center gap-2 text-start border-0 rounded-3"
                    style={{ padding: "7px 8px", background: isCurrent ? "rgba(15, 53, 81, 0.06)" : "transparent" }}
                  >
                    <span style={{ ...iconStyle, background: item.theme.gradient, color: "#ffffff" }}>{item.icon}</span>
                    <span className="flex-grow-1 overflow-hidden">
                      <span className="d-block fw-semibold text-truncate" style={{ color: "#12314d", fontSize: "0.9rem", lineHeight: 1.2 }}>{item.name}</span>
                      <span className="d-block text-truncate" style={{ color: "#6e8c9c", fontSize: 11 }}>{item.caption}</span>
                    </span>
                    {isCurrent ? <span className="icon" style={{ color: item.theme.solid, fontSize: 13 }}>{"\uf00c"}</span> : null}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  };

  // โลโก้ + ชื่อหน่วยงานของระบบแยก แทนโลโก้ ParthenonTech (ระบบที่ยังไม่มีโลโก้จะใช้ไอคอนของระบบแทน)
  // size: "lg" (sidebar ปกติ) | "sm" (หัวมือถือ) | "compact" (sidebar ย่อ: โลโก้อย่างเดียว ไม่มีชื่อ)
  const renderSystemMark = (size) => {
    const isLarge = size === "lg";
    const isCompact = size === "compact";
    const logo = systemLogos[activeSystem.key];
    const brand = activeSystem.brand || { th: activeSystem.name, en: "" };

    return (
      <div className="d-flex align-items-center gap-2" style={{ minHeight: isLarge ? 52 : 40, minWidth: 0 }}>
        {logo ? (
          <img
            src={logo}
            alt={brand.en || activeSystem.name}
            // ไฟล์โลโก้เป็น JPG พื้นขาว ใช้ multiply ให้พื้นขาวกลืนไปกับพื้นหลังของ navbar
            style={{ height: isLarge ? 54 : isCompact ? 50 : 42, width: "auto", flexShrink: 0, objectFit: "contain", mixBlendMode: "multiply" }}
          />
        ) : (
          <span style={{ ...iconStyle, width: isLarge || isCompact ? 42 : 36, height: isLarge || isCompact ? 42 : 36, fontSize: isLarge || isCompact ? 18 : 16, background: theme.gradient, color: "#ffffff" }}>
            {activeSystem.icon}
          </span>
        )}
        {!isCompact ? (
          <span style={{ minWidth: 0, lineHeight: 1.25 }}>
            <span className="d-block fw-bold" style={{ color: "#12314d", fontSize: isLarge ? "0.95rem" : "0.9rem" }}>{brand.th}</span>
            {brand.en ? <span className="d-block" style={{ color: "#5e8192", fontSize: isLarge ? 10.5 : 10, letterSpacing: "0.02em" }}>{brand.en}</span> : null}
          </span>
        ) : null}
      </div>
    );
  };

  return (
    <>
      <div
        className="position-fixed top-0 end-0 d-none d-xl-flex align-items-center justify-content-between px-4 px-xxl-5"
        style={{
          ...shellBackground,
          left: sidebarWidth,
          height: 66,
          transition: `left 260ms ${sidebarEasing}`,
          zIndex: 1035,
          borderTop: "0",
          borderLeft: "0",
          borderRight: "0",
          boxShadow: "0 14px 34px rgba(8, 46, 78, 0.08)",
        }}
      >
        <div>
          <div className="h5 fw-bold mb-0" style={{ color: "#12314d" }}>
            {title}
          </div>
          <small className="text-secondary">{description}</small>
        </div>

        <div className="d-flex align-items-center gap-3 position-relative">
          <div
            className="d-flex align-items-center gap-3 px-3 py-2"
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            style={{
              borderRadius: 18,
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.84) 0%, rgba(237,246,248,0.74) 100%)",
              boxShadow: "inset 0 0 0 1px rgba(15, 53, 81, 0.08)",
              cursor: "pointer",
            }}
          >
            <span
              className="text-white rounded-circle d-flex align-items-center justify-content-center fw-semibold"
              style={{
                width: 42,
                height: 42,
                background: theme.gradient,
                boxShadow: `0 8px 18px ${theme.shadow}`,
              }}
            >
              {initials || "-"}
            </span>
            <div className="overflow-hidden">
              <div
                className="fw-semibold text-truncate"
                style={{ color: "#12314d", maxWidth: 180 }}
              >
                {displayName}
              </div>
              <div
                className="small text-truncate"
                style={{ color: "#6e8c9c", maxWidth: 180 }}
              >
                {profile?.email || "-"}
              </div>
            </div>
          </div>

          {showProfileMenu && (
            <>
              <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1040 }} onClick={() => setShowProfileMenu(false)} />
              <div className="position-absolute bg-white rounded-4 shadow p-2" style={{ top: '120%', right: 0, width: 220, zIndex: 1045, border: "1px solid rgba(0,0,0,0.05)" }}>
                <button className="btn btn-light w-100 text-start px-3 py-2 mb-1 border-0 rounded-3 text-dark bg-transparent fw-medium" onClick={() => { setShowProfileMenu(false); setShowProfileModal(true); }}>
                  ข้อมูลโปรไฟล์
                </button>
                <button className="btn btn-light w-100 text-start px-3 py-2 border-0 rounded-3 text-danger bg-transparent fw-medium" onClick={() => { setShowProfileMenu(false); handleLogout(); }}>
                  ออกจากระบบ
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {mobileOpen && (
        <div
          className="d-xl-none"
          onClick={() => setMobileOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(6, 22, 36, 0.42)",
            backdropFilter: "blur(3px)",
            WebkitBackdropFilter: "blur(3px)",
            zIndex: 1035,
          }}
        />
      )}

      <header
        className="position-fixed top-0 start-0 w-100 d-xl-none"
        style={{
          ...shellBackground,
          zIndex: 1040,
          borderRadius: 0,
          boxShadow: "0 14px 34px rgba(8, 46, 78, 0.12)",
        }}
      >
        <div className="d-flex align-items-center justify-content-between px-3 py-1">
          <NavLink
            to={isMainSystem ? "/dashboard" : activeSystem.home}
            className="text-decoration-none d-flex align-items-center gap-3"
          >
            {isMainSystem ? (
              <div className="d-flex align-items-center gap-2">
                <img
                  src={LOGO512}
                  alt="ParthenonTech symbol"
                  style={{ width: 40, height: 40, objectFit: "contain" }}
                />
                <img
                  src={LOGO_TEXT}
                  alt="ParthenonTech"
                  style={{ height: 30, width: "auto", objectFit: "contain" }}
                />
              </div>
            ) : (
              renderSystemMark("sm")
            )}
          </NavLink>

          <button
            type="button"
            onClick={() => setMobileOpen((open) => !open)}
            className="btn border-0 d-flex align-items-center justify-content-center"
            style={{
              width: 46,
              height: 46,
              borderRadius: 16,
              background:
                "linear-gradient(135deg, rgba(15, 107, 220, 0.10) 0%, rgba(39, 214, 221, 0.18) 100%)",
              boxShadow: "inset 0 0 0 1px rgba(15, 53, 81, 0.08)",
            }}
          >
            <span className="icon text-dark">{mobileOpen ? "\uf00d" : "\uf0c9"}</span>
          </button>
        </div>

        {mobileOpen && (
          <div
            ref={mobileMenuRef}
            onScroll={handleMobileMenuScroll}
            className="px-3 pb-3"
            style={{
              maxHeight: "calc(100vh - 84px)",
              overflowY: "auto",
            }}
          >
            {availableSystems.length > 1 ? <div className="mb-2">{renderSystemSwitcher()}</div> : null}
            <div
              className="rounded-4 p-3"
              style={{
                background: "rgba(255,255,255,0.84)",
                boxShadow: "inset 0 0 0 1px rgba(15, 53, 81, 0.08)",
              }}
            >
              {menuGroups.map((group) => (
                <div key={group.title} className="mb-3">
                  <div
                    className="px-2 mb-2 small text-uppercase fw-semibold"
                    style={{ color: "#5e8192", letterSpacing: "0.08em" }}
                  >
                    {group.title}
                  </div>
                  <div className="d-grid gap-2">
                    {group.items.map((item) => renderMenuLink(item))}
                  </div>
                </div>
              ))}
              <div className="mt-4 pt-3 border-top">
                <button className="btn btn-light w-100 text-start mb-2 rounded-3 text-dark fw-medium" onClick={() => { setMobileOpen(false); setShowProfileModal(true); }}>แก้ไขโปรไฟล์</button>
                <button className="btn w-100 text-start text-danger bg-transparent border-0 rounded-3 fw-medium" onClick={() => { setMobileOpen(false); handleLogout(); }}>ออกจากระบบ</button>
              </div>
            </div>
          </div>
        )}
      </header>

      <aside
        className="position-fixed top-0 start-0 d-none d-xl-flex flex-column"
        style={{
          ...shellBackground,
          zIndex: 1036, // สูงกว่าแถบหัวเรื่อง (1035) เพื่อให้ปุ่มย่อที่ยื่นออกขอบขวาไม่โดนทับ
          width: sidebarWidth,
          height: "100vh",
          borderRadius: 0,
          boxShadow: "18px 0 40px rgba(8, 46, 78, 0.12)",
          transition: `width 260ms ${sidebarEasing}`,
        }}
      >
        {/* ปุ่มย่อ/ขยาย sidebar: ลอยคาบขอบขวา ชิดใต้แถบหัวเรื่อง (สูง 66px) ให้พอดีไม่โดนแถบทับ */}
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-label={collapsed ? "ขยายเมนู" : "ย่อเมนู"}
          aria-expanded={!collapsed}
          title={collapsed ? "ขยายเมนู" : "ย่อเมนู"}
          className="ptn-nav-toggle btn p-0 d-flex align-items-center justify-content-center"
          style={{
            position: "absolute",
            top: 20,
            right: -15,
            width: 30,
            height: 30,
            borderRadius: "50%",
            border: "2px solid rgba(255,255,255,0.92)",
            background: theme.gradient,
            color: "#ffffff",
            boxShadow: `0 8px 20px ${theme.shadow}, 0 0 0 4px rgba(255,255,255,0.28)`,
            zIndex: 2,
          }}
        >
          <span
            className="icon"
            style={{
              fontSize: 11,
              lineHeight: 1,
              display: "inline-block",
              marginLeft: collapsed ? 0 : -1,
              transform: collapsed ? "rotate(180deg)" : "rotate(0deg)",
              transition: `transform 320ms ${sidebarEasing}`,
            }}
          >
            {"\uf053"}
          </span>
        </button>

        <div className="mb-1 rounded-5 p-2">
          <NavLink
            to={isMainSystem ? "/dashboard" : activeSystem.home}
            className="text-decoration-none d-block"
          >
            <div className="d-flex align-items-center justify-content-center position-relative" style={{ minHeight: 56 }}>
              {isMainSystem ? (
                collapsed ? (
                  <img
                    src={LOGO512}
                    alt="ParthenonTech"
                    style={{ width: 54, height: 48, objectFit: "contain", flexShrink: 0 }}
                  />
                ) : (
                  <>
                    <img
                      src={LOGO512}
                      alt="ParthenonTech symbol"
                      style={{
                        width: 60,
                        height: 52,
                        objectFit: "contain",
                        flexShrink: 0,
                      }}
                    />
                    <img
                      src={LOGO_TEXT}
                      alt="ParthenonTech"
                      style={{
                        width: "100%",
                        maxWidth: 170,
                        height: "auto",
                        objectFit: "contain",
                        display: "block",
                      }}
                    />
                  </>
                )
              ) : (
                renderSystemMark(collapsed ? "compact" : "lg")
              )}
              {!collapsed ? (
                <small className="position-absolute text-10 text-secondary" style={{right:20, bottom:-4}}>v.{appVersion}</small>
              ) : null}
            </div>
          </NavLink>
        </div>

        {availableSystems.length > 1 ? <div className="px-3 pt-2">{renderSystemSwitcher(collapsed)}</div> : null}

        <div
          ref={desktopMenuRef}
          onScroll={handleDesktopMenuScroll}
          className={`flex-grow-1 px-3 py-3 ${collapsed ? "overflow-y-auto overflow-x-hidden ptn-nav-scroll-hidden" : "overflow-scroll"}`}
        >
          {menuGroups.map((group, groupIndex) => (
            <div key={group.title} className={collapsed ? "mb-3" : "mb-4"}>
              {collapsed ? (
                // โหมดย่อไม่มีที่ให้ชื่อกลุ่ม ใช้เส้นบางคั่นกลุ่มแทน (กลุ่มแรกไม่ต้องมี)
                groupIndex > 0 ? (
                  <div
                    className="mx-auto mb-3"
                    style={{ width: 30, height: 1, background: `linear-gradient(90deg, transparent, ${theme.shadow}, transparent)` }}
                  />
                ) : null
              ) : (
                <div
                  className="px-2 mb-2 small text-uppercase fw-semibold text-10"
                  style={{ color: "#5e8192", letterSpacing: "0.08em" }}
                >
                  {group.title}
                </div>
              )}
              <div className="d-grid gap-2">{group.items.map((item) => renderMenuLink(item, collapsed))}</div>
            </div>
          ))}
        </div>

        <div
          className="p-2"
          style={{
            borderTop: "1px solid rgba(15, 53, 81, 0.08)",
          }}
        >
          {collapsed ? (
            <div className="py-1 d-flex flex-column align-items-center gap-2">
              <button
                type="button"
                className="ptn-nav-compact-icon btn border-0 p-0 rounded-circle d-flex align-items-center justify-content-center text-white fw-semibold"
                aria-label="ข้อมูลโปรไฟล์"
                onClick={() => {
                  setMenuTip(null);
                  setShowProfileModal(true);
                }}
                onMouseEnter={(event) => showMenuTip(event, displayName)}
                onMouseLeave={hideMenuTip}
                style={{
                  width: 36,
                  height: 36,
                  fontSize: "0.85rem",
                  background: theme.gradient,
                  boxShadow: `0 8px 18px ${theme.shadow}`,
                }}
              >
                {initials || "-"}
              </button>
              <button
                type="button"
                className="ptn-nav-compact-icon btn btn-sm border-0 p-0 text-danger d-flex align-items-center justify-content-center"
                aria-label="ออกจากระบบ"
                onClick={() => {
                  setMenuTip(null);
                  handleLogout();
                }}
                onMouseEnter={(event) => showMenuTip(event, "ออกจากระบบ")}
                onMouseLeave={hideMenuTip}
                style={{ width: 32, height: 32, borderRadius: 10, background: "transparent" }}
              >
                <span className="icon" style={{ fontSize: "14px" }}>{"\uf2f5"}</span>
              </button>
            </div>
          ) : (
            <div
              className="rounded-4 p-2 d-flex align-items-center justify-content-between"
              style={{
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.86) 0%, rgba(237,246,248,0.76) 100%)",
                boxShadow: "inset 0 0 0 1px rgba(15, 53, 81, 0.08)",
              }}
            >
              <div className="d-flex align-items-center gap-2 overflow-hidden">
                <span
                  className="text-white rounded-circle d-flex align-items-center justify-content-center fw-semibold flex-shrink-0"
                  style={{
                    width: 42,
                    height: 42,
                    background: theme.gradient,
                    boxShadow: `0 8px 18px ${theme.shadow}`,
                  }}
                >
                  {initials || "-"}
                </span>
                <div className="overflow-hidden">
                  <div
                    className="fw-semibold text-truncate"
                    style={{ color: "#12314d", fontSize: "0.9rem" }}
                  >
                    {displayName}
                  </div>
                  <div className="small text-truncate" style={{ color: "#5f7d98", fontSize: "0.75rem" }}>
                    {profile?.email || "-"}
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center gap-1 flex-shrink-0">
                <button
                  className="btn btn-sm border-0 p-0 text-secondary d-flex align-items-center justify-content-center"
                  title="ข้อมูลโปรไฟล์"
                  onClick={() => setShowProfileModal(true)}
                  style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(0,0,0,0.04)" }}
                >
                  <span className="icon" style={{ fontSize: "14px" }}>{"\uf013"}</span>
                </button>
                <button
                  className="btn btn-sm border-0 p-0 text-danger d-flex align-items-center justify-content-center"
                  title="ออกจากระบบ"
                  onClick={handleLogout}
                  style={{ width: 32, height: 32, borderRadius: 8, background: "transparent" }}
                >
                  <span className="icon" style={{ fontSize: "14px" }}>{"\uf2f5"}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* tooltip ของ sidebar โหมดย่อ (จอใหญ่เท่านั้น) */}
      {collapsed && menuTip ? (
        <div
          className="d-none d-xl-block"
          role="tooltip"
          style={{
            position: "fixed",
            left: sidebarWidth + 14,
            top: menuTip.top,
            transform: "translateY(-50%)",
            zIndex: 1050,
            pointerEvents: "none",
            padding: "7px 14px",
            borderRadius: 12,
            background: "linear-gradient(135deg, rgba(12, 36, 58, 0.96) 0%, rgba(18, 49, 77, 0.94) 100%)",
            color: "#f4fbff",
            fontSize: "0.85rem",
            fontWeight: 600,
            whiteSpace: "nowrap",
            boxShadow: "0 14px 32px rgba(8, 46, 78, 0.28)",
            border: "1px solid rgba(255,255,255,0.12)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            animation: "ptnNavTipIn 160ms ease-out",
          }}
        >
          <span
            style={{
              position: "absolute",
              left: -5,
              top: "50%",
              width: 10,
              height: 10,
              transform: "translateY(-50%) rotate(45deg)",
              background: "rgba(12, 36, 58, 0.96)",
              borderLeft: "1px solid rgba(255,255,255,0.12)",
              borderBottom: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 2,
            }}
          />
          {menuTip.label}
        </div>
      ) : null}

      {/* Modal จัดการ Profile */}
      <Modal show={showProfileModal} onHide={() => setShowProfileModal(false)} centered>
        <Modal.Header closeButton className="bg-light border-0">
          <Modal.Title className="fw-bold h5 mb-0">ข้อมูลส่วนตัว (Profile)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-4">
            <div
              className="text-white rounded-circle d-flex align-items-center justify-content-center fw-semibold mx-auto mb-3"
              style={{ width: 80, height: 80, fontSize: "2rem", background: "linear-gradient(135deg, #0f6bdc 0%, #1e89e4 48%, #27d6dd 100%)", boxShadow: "0 8px 18px rgba(15, 107, 220, 0.18)" }}
            >
              {initials || "-"}
            </div>
            <h5 className="mb-0 fw-bold text-dark">{displayName}</h5>
            <div className="text-secondary mb-2">{profile?.email || "-"}</div>
            <span className="badge bg-primary bg-opacity-10 text-white px-3 py-2 rounded-pill text-uppercase">{profile?.user_type?.replace("_", " ") || "-"}</span>
          </div>

          <div className="border-top pt-3">
            <div className="fw-semibold text-dark mb-1">เปลี่ยนรหัสผ่าน</div>
            <div className="text-secondary small mb-3">เปลี่ยนแล้วระบบจะออกจากระบบทุกอุปกรณ์ ต้องเข้าสู่ระบบใหม่</div>
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label text-secondary small mb-1">รหัสผ่านปัจจุบัน</label>
                <input
                  type="password"
                  className="form-control rounded-3"
                  autoComplete="current-password"
                  value={passwordForm.current_password}
                  onChange={(event) => setPasswordForm((current) => ({ ...current, current_password: event.target.value }))}
                  placeholder="••••••••"
                />
              </div>
              <div className="col-12">
                <label className="form-label text-secondary small mb-1">รหัสผ่านใหม่ <span className="text-secondary">(อย่างน้อย 8 ตัวอักษร)</span></label>
                <input
                  type="password"
                  className="form-control rounded-3"
                  autoComplete="new-password"
                  value={passwordForm.new_password}
                  onChange={(event) => setPasswordForm((current) => ({ ...current, new_password: event.target.value }))}
                  placeholder="••••••••"
                />
              </div>
              <div className="col-12">
                <label className="form-label text-secondary small mb-1">ยืนยันรหัสผ่านใหม่</label>
                <input
                  type="password"
                  className="form-control rounded-3"
                  autoComplete="new-password"
                  value={passwordForm.confirm_password}
                  onChange={(event) => setPasswordForm((current) => ({ ...current, confirm_password: event.target.value }))}
                  onKeyDown={(event) => event.key === "Enter" && handleChangePassword()}
                  placeholder="••••••••"
                />
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="bg-light border-0">
          <button className="btn btn-secondary px-4 rounded-3" onClick={() => setShowProfileModal(false)}>ปิด</button>
          <button className="btn btn-primary px-4 rounded-3" onClick={handleChangePassword}>เปลี่ยนรหัสผ่าน</button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
